import { useQuery } from '@tanstack/react-query';
import {
  Package,
  TrendingUp,
  Sparkles,
  DollarSign,
  Search,
  Layers,
  Rocket,
  ArrowRight,
  CheckCircle2,
  Clock,
  ShoppingCart,
  Trophy,
  Target,
  Zap,
  Upload,
  BarChart3,
} from 'lucide-react';
import { api } from '../api/client';
import { Link } from 'react-router-dom';
import clsx from 'clsx';
import { DashboardSkeleton } from '../components/ui/Skeleton';
import ReviewQueue from '../components/ReviewQueue';

function formatCurrency(cents: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
  }).format(cents / 100);
}

export default function Dashboard() {
  const { data: listings, isLoading: listingsLoading } = useQuery({
    queryKey: ['listings'],
    queryFn: () => api.listings.list(),
  });

  const { data: niches, isLoading: nichesLoading } = useQuery({
    queryKey: ['niches'],
    queryFn: () => api.niches.list(),
  });

  const { data: dashboard, isLoading: dashboardLoading } = useQuery({
    queryKey: ['analytics-dashboard'],
    queryFn: () => api.analytics.dashboard(),
  });

  const isLoading = listingsLoading || nichesLoading || dashboardLoading;

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  // Counts
  const totalListings = listings?.listings?.length ?? 0;
  const readyToPublish = listings?.listings?.filter((l) => l.status === 'ready').length ?? 0;
  const published = listings?.listings?.filter((l) => l.printify_product_id).length ?? 0;
  const activeNiches = niches?.niches?.length ?? 0;

  // Sales data
  const totalOrders = dashboard?.overall?.total_orders ?? 0;
  const totalRevenue = dashboard?.overall?.total_revenue_cents ?? 0;
  const topRecipe = dashboard?.top_recipes?.[0];
  const topNiche = dashboard?.top_niches?.[0];

  // Determine user state
  const isNewUser = activeNiches === 0 && totalListings === 0;
  const hasNichesOnly = activeNiches > 0 && totalListings === 0;
  const hasUnpublished = readyToPublish > 0;
  const hasPublished = published > 0;
  const hasSales = totalOrders > 0;

  return (
    <div className="space-y-8">
      {/* Header with greeting */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          {isNewUser ? 'Welcome to EtsyFlow AI' : 'Dashboard'}
        </h1>
        <p className="text-gray-500 mt-1">
          {isNewUser
            ? 'Start your POD journey in 3 simple steps'
            : hasSales
              ? 'Your store is generating sales!'
              : 'Keep building your catalog'}
        </p>
      </div>

      {/* New User - Welcome Flow */}
      {isNewUser && (
        <div className="card bg-gradient-to-br from-orange-50 to-amber-50 border-orange-200">
          <div className="text-center py-8">
            <div className="w-20 h-20 bg-etsy-orange/10 rounded-full flex items-center justify-center mx-auto mb-6">
              <Rocket className="w-10 h-10 text-etsy-orange" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Let's launch your first product!
            </h2>
            <p className="text-gray-600 mb-8 max-w-lg mx-auto">
              EtsyFlow AI helps you research trending niches, generate designs with AI, and publish
              to Printify - all in one workflow.
            </p>

            {/* 3-Step Flow */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-3xl mx-auto mb-8">
              <div className="p-4 bg-white rounded-xl border border-orange-200">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Search className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-sm font-semibold text-gray-900 mb-1">1. Research</div>
                <p className="text-xs text-gray-500">Find trending niches with AI analysis</p>
              </div>
              <div className="p-4 bg-white rounded-xl border border-orange-200">
                <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Sparkles className="w-5 h-5 text-purple-600" />
                </div>
                <div className="text-sm font-semibold text-gray-900 mb-1">2. Generate</div>
                <p className="text-xs text-gray-500">Create designs with AI in bulk</p>
              </div>
              <div className="p-4 bg-white rounded-xl border border-orange-200">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Upload className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-sm font-semibold text-gray-900 mb-1">3. Publish</div>
                <p className="text-xs text-gray-500">Push to Printify & Etsy instantly</p>
              </div>
            </div>

            <Link to="/research" className="btn btn-primary text-lg px-8 py-3">
              <Search className="w-5 h-5 mr-2" />
              Start Researching Niches
            </Link>
          </div>
        </div>
      )}

      {/* Has Niches but No Designs */}
      {hasNichesOnly && (
        <div className="card bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
          <div className="flex items-start gap-6">
            <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Layers className="w-7 h-7 text-purple-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                You have {activeNiches} niche{activeNiches > 1 ? 's' : ''} researched!
              </h2>
              <p className="text-gray-600 mb-4">
                Time to generate some designs. Use batch generation to create multiple variants
                quickly.
              </p>
              <div className="flex items-center gap-3">
                <Link to="/batch" className="btn btn-primary flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Batch Generate Designs
                </Link>
                <Link
                  to="/research"
                  className="text-purple-600 hover:text-purple-800 font-medium flex items-center gap-1"
                >
                  Research more niches
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Has Unpublished Designs */}
      {hasUnpublished && (
        <div className="card bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
          <div className="flex items-start gap-6">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Upload className="w-7 h-7 text-green-600" />
            </div>
            <div className="flex-1">
              <h2 className="text-xl font-bold text-gray-900 mb-1">
                {readyToPublish} design{readyToPublish > 1 ? 's' : ''} ready to publish!
              </h2>
              <p className="text-gray-600 mb-4">
                Review and publish your designs to Printify to start making sales.
              </p>
              <div className="flex items-center gap-3">
                <Link
                  to="/inventory?status=ready"
                  className="btn btn-primary flex items-center gap-2"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  Review & Publish
                </Link>
                <Link
                  to="/batch"
                  className="text-green-600 hover:text-green-800 font-medium flex items-center gap-1"
                >
                  Generate more
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Auto-Scout Review Queue */}
      {!isNewUser && <ReviewQueue limit={5} />}

      {/* Stats Grid - Show for users with data */}
      {!isNewUser && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {hasSales && (
            <div className="card bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-green-500">
                  <DollarSign className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-green-700">Revenue</p>
                  <p className="text-xl font-bold text-green-900">{formatCurrency(totalRevenue)}</p>
                </div>
              </div>
            </div>
          )}

          {hasSales && (
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-blue-500">
                  <ShoppingCart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Orders</p>
                  <p className="text-xl font-bold">{totalOrders}</p>
                </div>
              </div>
            </div>
          )}

          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-purple-500">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Published</p>
                <p className="text-xl font-bold">{published}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-lg bg-amber-500">
                <TrendingUp className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-xs text-gray-500">Niches</p>
                <p className="text-xl font-bold">{activeNiches}</p>
              </div>
            </div>
          </div>

          {!hasSales && (
            <div className="card">
              <div className="flex items-center gap-3">
                <div className="p-2.5 rounded-lg bg-gray-400">
                  <Clock className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-500">Ready</p>
                  <p className="text-xl font-bold">{readyToPublish}</p>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Top Performers - Only if has sales */}
      {hasSales && (topRecipe || topNiche) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {topRecipe && topRecipe.total_orders > 0 && (
            <div className="card border-2 border-green-200 bg-green-50/50">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Trophy className="w-6 h-6 text-green-600" />
                </div>
                <div className="flex-1">
                  <span className="text-xs font-semibold text-green-600 uppercase tracking-wide">
                    Top Recipe
                  </span>
                  <h3 className="text-lg font-bold text-gray-900">{topRecipe.recipe_name}</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    <span className="font-semibold text-green-600">
                      {formatCurrency(topRecipe.total_revenue_cents)}
                    </span>{' '}
                    from {topRecipe.total_orders} orders
                  </p>
                  <Link
                    to={`/batch?recipe_id=${topRecipe.recipe_id}`}
                    className="inline-flex items-center gap-2 text-green-700 hover:text-green-900 font-semibold text-sm"
                  >
                    <Zap className="w-4 h-4" />
                    Create more with this recipe
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}

          {topNiche && topNiche.total_orders > 0 && (
            <div className="card border-2 border-blue-200 bg-blue-50/50">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Target className="w-6 h-6 text-blue-600" />
                </div>
                <div className="flex-1">
                  <span className="text-xs font-semibold text-blue-600 uppercase tracking-wide">
                    Best Niche
                  </span>
                  <h3 className="text-lg font-bold text-gray-900">{topNiche.niche_keyword}</h3>
                  <p className="text-gray-600 text-sm mb-3">
                    <span className="font-semibold text-blue-600">
                      {formatCurrency(topNiche.total_revenue_cents)}
                    </span>{' '}
                    from {topNiche.total_orders} orders
                  </p>
                  <Link
                    to={`/batch?niche_keyword=${encodeURIComponent(topNiche.niche_keyword)}`}
                    className="inline-flex items-center gap-2 text-blue-700 hover:text-blue-900 font-semibold text-sm"
                  >
                    <Sparkles className="w-4 h-4" />
                    Add more designs to this niche
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Quick Actions Grid - For active users */}
      {!isNewUser && !hasNichesOnly && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/research"
            className={clsx(
              'card hover:border-blue-300 hover:bg-blue-50/50 transition-colors group',
              'flex items-center gap-4'
            )}
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
              <Search className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Research Niches</h3>
              <p className="text-sm text-gray-500">Find new opportunities</p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 ml-auto group-hover:text-blue-500 transition-colors" />
          </Link>

          <Link
            to="/batch"
            className={clsx(
              'card hover:border-purple-300 hover:bg-purple-50/50 transition-colors group',
              'flex items-center gap-4'
            )}
          >
            <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
              <Layers className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Batch Generate</h3>
              <p className="text-sm text-gray-500">Create multiple designs</p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 ml-auto group-hover:text-purple-500 transition-colors" />
          </Link>

          <Link
            to="/analytics"
            className={clsx(
              'card hover:border-orange-300 hover:bg-orange-50/50 transition-colors group',
              'flex items-center gap-4'
            )}
          >
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center group-hover:bg-orange-200 transition-colors">
              <BarChart3 className="w-6 h-6 text-etsy-orange" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">View Analytics</h3>
              <p className="text-sm text-gray-500">Track performance</p>
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 ml-auto group-hover:text-orange-500 transition-colors" />
          </Link>
        </div>
      )}

      {/* Published but No Sales - Encouragement */}
      {hasPublished && !hasSales && (
        <div className="card bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                <Clock className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">
                  {published} product{published > 1 ? 's' : ''} live on Printify
                </h3>
                <p className="text-sm text-gray-600">
                  Sales take time. Keep adding products to increase your chances!
                </p>
              </div>
            </div>
            <Link to="/batch" className="btn btn-secondary flex items-center gap-2 flex-shrink-0">
              <Sparkles className="w-4 h-4" />
              Add More Products
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
